import { useState } from "react"
import { useRouter } from "next/router"
import NextLink from "next/link"

// libs
import _ from "lodash"

// @configs
import routes from "@configs/routes"

// @core
import { useGlobalContext, usePropertyContext } from "@core/context"

// mui
import { Box, Typography } from "@mui/material"
import { MenuItem, TextField } from '@mui/material';

// snippets
import DeveloperBadge from "../snippets/DeveloperBadge"

// @components
import { Link } from "@components/Link"

// types
import type { ResponseComplexItem, ResponseComplexList } from "@core/api/useEstate/types"


interface Props {
  // items: ItemProps[]
}


export default function ComplexesList(props: Props) {

  // states
  const [selectedLocation, setSelectedLocation] = useState<string>('all')

  // context
  const globalContext = useGlobalContext()

  // computed
  const complexesList: ResponseComplexList = globalContext.pageProps.complexesList
  const complexesListByLocation = complexesList.results.filter((item) => selectedLocation === 'all' || item.parent_location?.slug === selectedLocation)

  // const items = demoItems

  const renderLocations = () => {
    const locations = complexesList.results.filter((item) => item.parent_location).map((item) => item.parent_location)

    // unique
    const uniqueLocations = _.uniqBy(locations, 'slug')

    // order by name
    const orderedLocations = _.orderBy(uniqueLocations, ['name'], ['asc'])

    return (
      <TextField
        sx={{
          minWidth: {
            xs: '100%',
            md: 300,
          }
        }}
        select
        label="Location"
        variant="outlined"
        value={selectedLocation}>
        <MenuItem
          value="all"
          onClick={() => {
            setSelectedLocation('all')
          }}>
          All
        </MenuItem>

        {orderedLocations.map((item, index) => (
          <MenuItem key={index} value={item?.slug} onClick={() => {
            if (item?.slug) {
              setSelectedLocation(item.slug)
            }
          }}>
            {item?.name}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  return (
    <Box>
      <Box sx={{
        pb: 10,
        mb: 10,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        {renderLocations()}
      </Box>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: 5,
      }}>

        {complexesListByLocation.map((item, index) => {
          return (
            <Item key={index} item={item} />
          )
        }
        )}
      </Box>
    </Box>
  )

}


interface ItemProps {
  item: ResponseComplexItem
}
const Item = ({ item }: ItemProps) => {

  // hooks
  const router = useRouter()

  // context
  const propertyContext = usePropertyContext()

  // computed
  const price = propertyContext.calculatePrice(item.price)

  const href = routes.CATALOG.COMPLEXESES.COMPLEX.replace('[slug]', item.slug)

  return (
    <Box
      onClick={() => {
        router.push(href)
      }}
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 2,
        overflow: 'hidden',
        transition: '0.3s',
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 4,
        }
      }}>

      {item.preview?.small && (
        <Box component="img"
          src={item.preview.small}
          alt={item.title}
          sx={{ width: '100%' }} />
      )}

      <Box sx={{
        p: 5,
      }}>
        <Link variant="h5" component="h3" href={href} color="inherit">
          {item.title}
        </Link>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {item.address}
        </Typography>
        <Typography variant="h6">
          From {price}
        </Typography>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mt: 4,
          pt: 4,
          gap: 3,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}>
          {item.developers.map((developer, index) => (
            <DeveloperBadge key={index} company={developer} />
          ))}
        </Box>
      </Box>
    </Box>
  )
}


